import React, { useState, useEffect, forwardRef } from "react";
import {
  buildAverageRow,
  applyDifferenceToRows
} from "../../utils/energiaUtils";

const EnergiaBlockLinked = forwardRef(({ block, onSaveBlock }, ref) => {
  const [tableData, setTableData] = useState(
    block || {
      title: "",
      columns: [],
      rows: [],
      summary: "",
    }
  );

  const [isEditing, setIsEditing] = useState(false);
  const [original, setOriginal] = useState(null);

  useEffect(() => {
    if (!isEditing && block) {
      setTableData(block);
    }
  }, [block, isEditing]);

  if (!block) {
    return <div>Energia-tietoja ei ole vielä saatavilla</div>;
  }

  // -----------------------------
  // COLUMN REMOVE
  // -----------------------------
  const removeColumn = (colIndex) => {
    const columnName = tableData.columns[colIndex];

    const confirmed = window.confirm(
      `Haluatko varmasti poistaa sarakkeen "${columnName}"?`
    );

    if (!confirmed) return;

    const updatedColumns = tableData.columns.filter(
      (_, i) => i !== colIndex
    );

    const updatedRows = tableData.rows.map((row) => {
      const newRow = { ...row };
      delete newRow[columnName];
      return newRow;
    });

    setTableData({
      ...tableData,
      columns: updatedColumns,
      rows: updatedRows,
    });
  };

  // -----------------------------
  // SAVE / CANCEL
  // -----------------------------
  const handleSave = () => {
    const fullBlock = {
      ...block,
      title: block.title,
      columns: tableData.columns,
      rows: tableData.rows,
      summary: tableData.summary,
    };

    onSaveBlock(fullBlock);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTableData(original);
    setIsEditing(false);
  };

  // -----------------------------
  // ROW OPERATIONS
  // -----------------------------
  const addRow = () => {
    const newRow = {};
    tableData.columns.forEach((c) => (newRow[c] = ""));
    setTableData({
      ...tableData,
      rows: [...tableData.rows, newRow],
    });
  };

  const removeRow = (idx) => {
    setTableData({
      ...tableData,
      rows: tableData.rows.filter((_, i) => i !== idx),
    });
  };

  const changeCell = (r, c, v) => {
    const updated = structuredClone(tableData);
    updated.rows[r][c] = v;
    setTableData(updated);
  };

  // -----------------------------
 
  // -----------------------------
  const processedRows = applyDifferenceToRows(
    tableData.rows,
    tableData.columns
  );

  const averageRow = buildAverageRow(
    tableData.columns,
    processedRows
  );

  const rowsWithAverage = averageRow
    ? [...processedRows, averageRow]
    : processedRows;

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div ref={ref}>
      {/* Buttons */}
      <div className="text-center mb-2">
        {isEditing ? (
          <>
            <button
              className="btn btn-sm btn-success me-2"
              onClick={handleSave}
            >
              Tallenna
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={handleCancel}
            >
              Peruuta
            </button>
          </>
        ) : (
          <button
            className="btn btn-sm btn-success"
            onClick={() => {
              setOriginal(structuredClone(tableData));
              setIsEditing(true);
            }}
          >
            Muokkaa
          </button>
        )}
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-sm table-bordered mb-3">
          <thead>
            <tr>
              {tableData.columns.map((c, i) => (
                <th
                  key={i}
                  className="bg-success text-white text-center position-relative"
                  style={{
                    verticalAlign: "middle",
                    paddingTop: "28px",
                  }}
                >
                  {isEditing && i !== 0 && (
                    <button
                      type="button"
                      onClick={() => removeColumn(i)}
                      className="btn btn-sm btn-outline-danger"
                      style={{
                        position: "absolute",
                        top: "4px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontSize: "11px",
                        lineHeight: "1",
                        padding: "2px 6px",
                      }}
                    >
                      ×
                    </button>
                  )}
                  {c}
                </th>
              ))}
              {isEditing && (
                <th className="bg-success text-white">Poista</th>
              )}
            </tr>
          </thead>

          <tbody>
            {rowsWithAverage.map((row, ri) => {
              const isAverage =
                row[tableData.columns[0]] === "Keskiarvo";

              return (
                <tr key={ri}>
                  {tableData.columns.map((col, ci) => (
                    <td
                      key={ci}
                      className="text-center"
                      style={
                        isAverage
                          ? {
                              backgroundColor: "#04aa00",
                              color: "white",
                              fontWeight: "bold",
                            }
                          : {}
                      }
                    >
                      {isEditing &&
                      !isAverage &&
                      col !== "Erotus" ? (
                        <input
                          type="text"
                          className="form-control form-control-sm text-center"
                          value={row[col] ?? ""}
                          onChange={(e) =>
                            changeCell(ri, col, e.target.value)
                          }
                        />
                      ) : (
                        row[col] || ""
                      )}
                    </td>
                  ))}

                  {isEditing && !isAverage && (
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeRow(ri)}
                      >
                        ×
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isEditing && (
        <button
          className="btn btn-sm btn-outline-primary mb-3"
          onClick={addRow}
        >
          + Lisää rivi
        </button>
      )}
    </div>
  );
});

export default EnergiaBlockLinked;